/**
 * 計助 V1 ワークスペース: 左・中央・右の3カラム（各カラムは上下2分割、移動バーでリサイズ）
 */

import {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type TouchEvent as ReactTouchEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-bootstrap';
import { useShallow } from 'zustand/react/shallow';
import { useAuth } from '../../../../hooks/useAuth';
import { isKeisukePublicEnabled } from '../../../../utils/config';
import { gridDataHasNumericValues } from '../../../../utils/keisukeGridData';
import {
  applyCenterRightColumnSplitFromViewportX,
  applyCenterVerticalSplitFromViewportY,
  applyLeftCenterColumnSplitFromViewportX,
  applyLeftVerticalSplitFromViewportY,
  applyRightVerticalSplitFromViewportY,
  useKeisukeDashboardStore,
} from '../../../../stores/systems/keisuke/dashboardStore';
import {
  loadLayoutFromStorage,
  persistLayoutToStorage,
} from './keisukeDashboardLayoutStorage';
import KeisukeDashboardExplorerPane from './KeisukeDashboardExplorerPane';
import KeisukeDashboardLeftPane from './KeisukeDashboardLeftPane';
import KeisukeDashboardCenterPane from './KeisukeDashboardCenterPane';
import { KeisukeDashboardBarChartSettingsProvider } from './KeisukeDashboardBarChartSettingsContext';
import { KeisukeDashboardDoseResponseSettingsProvider } from './KeisukeDashboardDoseResponseChart';
import { KeisukeDashboardSurvivalChartSettingsProvider } from './KeisukeDashboardSurvivalChart';
import KeisukeDashboardChartPanel from './KeisukeDashboardChartPanel';
import KeisukeDashboardChartSettingsSlot from './KeisukeDashboardChartSettingsSlot';
import KeisukeDashboardResultsStrip from './KeisukeDashboardResultsStrip';
import KeisukeDashboardOnboarding from './KeisukeDashboardOnboarding';
import {
  keisukeDashboardGridAreaUsesShrinkWrap,
  keisukeDashboardLeftRailIsSparse,
} from './keisukeDashboardDataGridLayout';
import { KeisukeChartSettingsUiProvider } from './keisukeChartSettingsUiContext';
import styles from './KeisukeDashboard.module.scss';

type SplitterKind =
  | 'leftCenter'
  | 'centerRight'
  | 'leftVert'
  | 'centerVert'
  | 'rightVert'
  | null;

const MOUSE_DRAG_MOVE_OPTS: AddEventListenerOptions = { capture: true, passive: false };
const MOUSE_DRAG_END_OPTS: AddEventListenerOptions = { capture: true };
export default function KeisukeDashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const dragKindRef = useRef<SplitterKind>(null);
  const [draggingSplitter, setDraggingSplitter] = useState<SplitterKind>(null);
  const [guestBannerVisible, setGuestBannerVisible] = useState(() => {
    try {
      // 閉じたら再訪でも出さない（session だとタブごとに毎回出る）
      return localStorage.getItem('keisuke_guest_banner_dismissed') !== '1';
    } catch {
      return true;
    }
  });
  /**
   * フォルダ／IndexedDB／サーバーに実ファイルがあるときだけマイデータ帯を広げる。
   * 空なら左はコンパクト（解析＋ツールバーのみ）。
   */
  const [explorerNeedsRoom, setExplorerNeedsRoom] = useState(false);
  /** グラフ設定は常に閉じて開始（開いたまま記憶すると図が圧迫される） */
  const [chartSettingsOpen, setChartSettingsOpen] = useState(false);
  const toggleChartSettingsOpen = useCallback(() => {
    setChartSettingsOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    try {
      // 旧「設定オープン記憶」キーの掃除（現在は記憶しない）
      sessionStorage.removeItem('keisuke_chart_settings_open');
    } catch {
      // ignore
    }
    const hideGuestBanner = () => setGuestBannerVisible(false);
    /** compact↔full の切替で Explorer が remount すると一時的に needsRoom=false になり API 連打するため、縮小のみ遅延する */
    let collapseTimer: ReturnType<typeof setTimeout> | undefined;
    const onExplorerNeedsRoom = (e: Event) => {
      const detail = (e as CustomEvent<{ needsRoom?: boolean }>).detail;
      const next = Boolean(detail?.needsRoom);
      if (next) {
        if (collapseTimer !== undefined) {
          clearTimeout(collapseTimer);
          collapseTimer = undefined;
        }
        setExplorerNeedsRoom((prev) => (prev ? prev : true));
        return;
      }
      if (collapseTimer !== undefined) clearTimeout(collapseTimer);
      collapseTimer = setTimeout(() => {
        collapseTimer = undefined;
        setExplorerNeedsRoom(false);
      }, 400);
    };
    window.addEventListener('keisuke-guest-banner-dismiss', hideGuestBanner);
    window.addEventListener('keisuke-explorer-needs-room', onExplorerNeedsRoom);
    return () => {
      if (collapseTimer !== undefined) clearTimeout(collapseTimer);
      window.removeEventListener('keisuke-guest-banner-dismiss', hideGuestBanner);
      window.removeEventListener('keisuke-explorer-needs-room', onExplorerNeedsRoom);
    };
  }, []);

  const {
    leftColumnRatio,
    centerColumnRatio,
    leftAnalysisFileRatio,
    centerDataResultsRatio,
    rightChartSettingsRatio,
    inputFormType,
    resultCache,
    lastErrorMessage,
  } = useKeisukeDashboardStore(
    useShallow((s) => ({
      leftColumnRatio: s.leftColumnRatio,
      centerColumnRatio: s.centerColumnRatio,
      leftAnalysisFileRatio: s.leftAnalysisFileRatio,
      centerDataResultsRatio: s.centerDataResultsRatio,
      rightChartSettingsRatio: s.rightChartSettingsRatio,
      inputFormType: s.inputFormType,
      resultCache: s.resultCache,
      lastErrorMessage: s.lastErrorMessage,
    }))
  );

  /** boolean だけ購読 → セル編集のたびに3列ルートが再描画されない */
  const hasNumericGrid = useKeisukeDashboardStore((s) =>
    gridDataHasNumericValues(s.gridHeaders, s.gridData)
  );

  /**
   * 結果帯: 数値があり、かつ成功キャッシュ／本エラーのときだけ。
   * isCalculating は含めない（debounce 再計算のたびにペインが開閉してちらつくため）。
   */
  const showResultsPane =
    hasNumericGrid && (Boolean(resultCache) || Boolean(lastErrorMessage));
  /**
   * t検定2列表は設定が中央へ移済みで左が薄い → 幅を抑えて中央・グラフへ余白を返す。
   */
  const leftRailSparse = keisukeDashboardLeftRailIsSparse(inputFormType);
  /**
   * グラフ列は結果キャッシュがあるときだけ。
   * 以前は薄い左レール時に空列を予約していたが、~700×980 の灰色砂漠になり空状態 UX を壊す。
   * サンプル読込で列が現れる一度のジャンプは許容する。
   */
  const showChartColumn = Boolean(resultCache);
  /**
   * コンパクト表＋結果: 結果は CenterPane の白カード内フッタへ埋め込み。
   * 長形式仮想表だけ外付け結果帯＋縦スプリッタ。
   */
  const centerDataShrinkToContent =
    showResultsPane && keisukeDashboardGridAreaUsesShrinkWrap(inputFormType);

  const centerResultsPaneStyle = useMemo((): CSSProperties => {
    return {
      flex: '0 1 auto',
      minHeight: 120,
      maxHeight: `${Math.round((1 - centerDataResultsRatio) * 100)}%`,
    };
  }, [centerDataResultsRatio]);
  useEffect(() => {
    const saved = loadLayoutFromStorage();
    const state = useKeisukeDashboardStore.getState();
    if (saved.leftColumnRatio != null) state.setLeftColumnRatio(saved.leftColumnRatio);
    if (saved.centerColumnRatio != null) state.setCenterColumnRatio(saved.centerColumnRatio);
    if (saved.leftAnalysisFileRatio != null) state.setLeftAnalysisFileRatio(saved.leftAnalysisFileRatio);
    if (saved.centerDataResultsRatio != null) state.setCenterDataResultsRatio(saved.centerDataResultsRatio);
    if (saved.rightChartSettingsRatio != null) state.setRightChartSettingsRatio(saved.rightChartSettingsRatio);
  }, []);

  useEffect(() => {
    const s = useKeisukeDashboardStore.getState();
    persistLayoutToStorage({
      leftColumnRatio: s.leftColumnRatio,
      centerColumnRatio: s.centerColumnRatio,
      leftAnalysisFileRatio: s.leftAnalysisFileRatio,
      centerDataResultsRatio: s.centerDataResultsRatio,
      rightChartSettingsRatio: s.rightChartSettingsRatio,
    });
  }, [
    leftColumnRatio,
    centerColumnRatio,
    leftAnalysisFileRatio,
    centerDataResultsRatio,
    rightChartSettingsRatio,
  ]);

  useEffect(() => {
    const { gridData, gridHeaders } = useKeisukeDashboardStore.getState();
    if (gridDataHasNumericValues(gridHeaders, gridData)) {
      useKeisukeDashboardStore.getState().runCurrentAnalysis();
    }
  }, []);

  const applyDrag = useCallback((kind: SplitterKind, clientX: number, clientY: number) => {
    if (kind === 'leftCenter') applyLeftCenterColumnSplitFromViewportX(clientX);
    else if (kind === 'centerRight') applyCenterRightColumnSplitFromViewportX(clientX);
    else if (kind === 'leftVert') applyLeftVerticalSplitFromViewportY(clientY);
    else if (kind === 'centerVert') applyCenterVerticalSplitFromViewportY(clientY);
    else if (kind === 'rightVert') applyRightVerticalSplitFromViewportY(clientY);
  }, []);

  const startMouseDrag = useCallback(
    (kind: SplitterKind, e: ReactMouseEvent<HTMLDivElement>) => {
      if (!kind || e.button !== 0) return;
      e.preventDefault();
      dragKindRef.current = kind;
      setDraggingSplitter(kind);
      const onMove = (ev: MouseEvent) => {
        ev.preventDefault();
        applyDrag(dragKindRef.current, ev.clientX, ev.clientY);
      };
      const onEnd = () => {
        window.removeEventListener('mousemove', onMove, MOUSE_DRAG_MOVE_OPTS);
        window.removeEventListener('mouseup', onEnd, MOUSE_DRAG_END_OPTS);
        dragKindRef.current = null;
        setDraggingSplitter(null);
      };
      window.addEventListener('mousemove', onMove, MOUSE_DRAG_MOVE_OPTS);
      window.addEventListener('mouseup', onEnd, MOUSE_DRAG_END_OPTS);
    },
    [applyDrag]
  );

  const startTouchDrag = useCallback(
    (kind: SplitterKind, e: ReactTouchEvent<HTMLDivElement>) => {
      if (!kind || e.touches.length !== 1) return;
      e.preventDefault();
      const touchId = e.touches[0].identifier;
      dragKindRef.current = kind;
      setDraggingSplitter(kind);
      const onMove = (ev: TouchEvent) => {
        const touch = Array.from(ev.touches).find((x) => x.identifier === touchId);
        if (!touch) return;
        ev.preventDefault();
        applyDrag(dragKindRef.current, touch.clientX, touch.clientY);
      };
      const onEnd = () => {
        window.removeEventListener('touchmove', onMove, MOUSE_DRAG_MOVE_OPTS);
        window.removeEventListener('touchend', onEnd, MOUSE_DRAG_END_OPTS);
        window.removeEventListener('touchcancel', onEnd, MOUSE_DRAG_END_OPTS);
        dragKindRef.current = null;
        setDraggingSplitter(null);
      };
      window.addEventListener('touchmove', onMove, MOUSE_DRAG_MOVE_OPTS);
      window.addEventListener('touchend', onEnd, MOUSE_DRAG_END_OPTS);
      window.addEventListener('touchcancel', onEnd, MOUSE_DRAG_END_OPTS);
    },
    [applyDrag]
  );

  const nudgeSplit = useCallback((kind: SplitterKind, delta: number) => {
    const st = useKeisukeDashboardStore.getState();
    if (kind === 'leftCenter') st.setLeftColumnRatio(st.leftColumnRatio + delta);
    else if (kind === 'centerRight') st.setCenterColumnRatio(st.centerColumnRatio + delta);
    else if (kind === 'leftVert') st.setLeftAnalysisFileRatio(st.leftAnalysisFileRatio + delta);
    else if (kind === 'centerVert') st.setCenterDataResultsRatio(st.centerDataResultsRatio + delta);
    else if (kind === 'rightVert') st.setRightChartSettingsRatio(st.rightChartSettingsRatio + delta);
  }, []);

  const splitterRatio = (kind: SplitterKind): number => {
    if (kind === 'leftCenter') return leftColumnRatio;
    if (kind === 'centerRight') return centerColumnRatio;
    if (kind === 'leftVert') return leftAnalysisFileRatio;
    if (kind === 'centerVert') return centerDataResultsRatio;
    if (kind === 'rightVert') return rightChartSettingsRatio;
    return 0.5;
  };

  const splitterLabel = (kind: SplitterKind): string => {
    switch (kind) {
      case 'leftCenter':
        return t('keisuke.dashboard.splitterLeftCenter');
      case 'centerRight':
        return t('keisuke.dashboard.splitterCenterRight');
      case 'leftVert':
        return t('keisuke.dashboard.splitterLeftVert');
      case 'centerVert':
        return t('keisuke.dashboard.splitterCenterVert');
      case 'rightVert':
        return t('keisuke.dashboard.splitterRightVert');
      default:
        return t('keisuke.dashboard.splitterLabel');
    }
  };

  const renderSplitter = (kind: SplitterKind, dir: 'v' | 'h') => {
    const ratio = splitterRatio(kind);
    return (
      <div
        role="separator"
        tabIndex={0}
        aria-orientation={dir === 'v' ? 'vertical' : 'horizontal'}
        aria-valuenow={Math.round(ratio * 100)}
        aria-valuemin={20}
        aria-valuemax={80}
        aria-label={splitterLabel(kind)}
        className={`${styles.splitter} ${dir === 'h' ? styles.horizontal : ''} ${draggingSplitter === kind ? styles.dragging : ''}`}
        onMouseDown={(e) => startMouseDrag(kind, e)}
        onTouchStart={(e) => startTouchDrag(kind, e)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            nudgeSplit(kind, -0.02);
          } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            nudgeSplit(kind, 0.02);
          }
        }}
      >
        <span className={styles.splitterBar} />
      </div>
    );
  };

  /**
   * 空状態でも保存済み left 比率を使う（旧: 最低 0.22 に広げていたが、
   * 中央表が全幅充填になったため左を無理に広げない）。
   * 薄い左レールは固定幅にし、余りを中央・グラフへ渡す（max-width だけだと余りが行に残る）。
   */
  const leftFlex = leftColumnRatio;
  const remainder = leftRailSparse ? 1 : 1 - leftFlex;
  const centerFlex = showChartColumn ? remainder * centerColumnRatio : remainder;
  const rightFlex = showChartColumn ? remainder * (1 - centerColumnRatio) : 0;

  const chartSettingsUi = useMemo(
    () => ({ open: chartSettingsOpen, toggle: toggleChartSettingsOpen }),
    [chartSettingsOpen, toggleChartSettingsOpen]
  );

  return (
    <KeisukeDashboardBarChartSettingsProvider>
      <KeisukeDashboardDoseResponseSettingsProvider>
        <KeisukeDashboardSurvivalChartSettingsProvider>
          <KeisukeChartSettingsUiProvider value={chartSettingsUi}>
            <div
              data-keisuke-dashboard-root
              className={styles.root}
              role="application"
              aria-label={t('keisuke.dashboard.ariaLabel')}
            >
            {/* emptyHero と手順重複しない。自動表示せず「使い方」イベントのみ。ゲストでも listener を残す */}
            <KeisukeDashboardOnboarding />
            {!user && isKeisukePublicEnabled() && guestBannerVisible && (
              <Alert
                variant="success"
                className="mx-2 mb-0 py-2 small flex-shrink-0"
                dismissible
                onClose={() => {
                  try {
                    localStorage.setItem('keisuke_guest_banner_dismissed', '1');
                    sessionStorage.removeItem('keisuke_guest_banner_dismissed');
                  } catch {
                    // ignore
                  }
                  setGuestBannerVisible(false);
                }}
              >
                {t('keisuke.guest_mode_banner')}
              </Alert>
            )}

            <div data-keisuke-workspace-body className={styles.workspaceBody}>
              <div data-keisuke-columns-row className={styles.columnsRow}>
                <div
                  data-keisuke-left-column
                  className={`${styles.column} ${styles.columnLeft}${
                    explorerNeedsRoom ? '' : ` ${styles.columnLeftCompact}`
                  }${leftRailSparse ? ` ${styles.columnLeftSparse}` : ''}`}
                  style={
                    leftRailSparse
                      ? { flex: '0 0 auto', minWidth: 0 }
                      : { flex: `${leftFlex} 1 0`, minWidth: 0 }
                  }
                >
                  {/*
                    Explorer は常に1インスタンス。compact/expanded で remount すると
                    needsRoom が空状態で false になり、展開⇔折りたたみの無限ループで
                    /filesystem/root を連打する（ERR_INSUFFICIENT_RESOURCES）。
                  */}
                  <div
                    className={styles.paneTop}
                    style={
                      explorerNeedsRoom
                        ? { flex: `${leftAnalysisFileRatio} 1 0`, minHeight: 0, overflow: 'hidden' }
                        : { flex: '0 0 auto', minHeight: 0, overflow: 'hidden' }
                    }
                    aria-label={t('keisuke.dashboard.leftPaneLabel')}
                  >
                    {explorerNeedsRoom ? (
                      <div className={styles.leftPaneFill}>
                        <KeisukeDashboardLeftPane />
                      </div>
                    ) : (
                      <div className={styles.leftPaneStack}>
                        <div className={styles.leftPaneStackBody}>
                          <KeisukeDashboardLeftPane />
                        </div>
                      </div>
                    )}
                  </div>
                  {explorerNeedsRoom ? renderSplitter('leftVert', 'h') : null}
                  <div
                    className={
                      explorerNeedsRoom ? styles.paneBottom : styles.paneBottomCompactInline
                    }
                    style={
                      explorerNeedsRoom
                        ? { flex: `${1 - leftAnalysisFileRatio} 1 0`, minHeight: 0 }
                        : undefined
                    }
                    aria-label={t('keisuke.dashboard.explorerLabel')}
                  >
                    <KeisukeDashboardExplorerPane compact={!explorerNeedsRoom} />
                  </div>
                </div>

                {!leftRailSparse ? renderSplitter('leftCenter', 'v') : (
                  <div className={styles.sparseLeftDivider} aria-hidden />
                )}

                <div
                  data-keisuke-center-column
                  className={`${styles.column} ${styles.columnCenter}`}
                  style={{ flex: `${centerFlex} 1 0`, minWidth: 0 }}
                >
                  <main
                    className={styles.paneTop}
                    style={{ flex: '1 1 0', minHeight: 0 }}
                    aria-label={t('keisuke.dashboard.centerPaneLabel')}
                  >
                    <KeisukeDashboardCenterPane />
                  </main>
                  {showResultsPane && !centerDataShrinkToContent ? (
                    <>
                      {renderSplitter('centerVert', 'h')}
                      <div
                        data-keisuke-results-pane
                        className={`${styles.paneBottom} ${styles.paneBottomResultsHug}`}
                        style={centerResultsPaneStyle}
                      >
                        <KeisukeDashboardResultsStrip />
                      </div>
                    </>
                  ) : null}
                </div>

                {showChartColumn && (
                  <>
                    {renderSplitter('centerRight', 'v')}

                    <div
                      data-keisuke-right-column
                      data-keisuke-chart-settings={chartSettingsOpen ? 'open' : 'closed'}
                      className={`${styles.column} ${styles.columnRight}`}
                      style={{ flex: `${rightFlex} 1 0`, minWidth: 0 }}
                    >
                      {/* 設定トグルは右ペイン上端（画面下端フッター隣だと見つからない） */}
                      {chartSettingsOpen && (
                        <>
                          <div
                            id="keisuke-chart-settings-panel"
                            className={`${styles.paneBottom} ${styles.chartSettingsPane}`}
                            style={{ flex: `${1 - rightChartSettingsRatio} 1 0`, minHeight: 0 }}
                            role="region"
                            aria-label={t('keisuke.workspace.chartSettingsBand')}
                          >
                            <div className={styles.chartSettingsToggleRow}>
                              <button
                                type="button"
                                className={styles.chartSettingsToggleBtn}
                                onClick={toggleChartSettingsOpen}
                                aria-expanded
                                aria-label={t('keisuke.workspace.hideChartSettings')}
                              >
                                <span>{t('keisuke.workspace.chartSettingsBand')}</span>
                                <i className="fas fa-chevron-up" aria-hidden />
                              </button>
                            </div>
                            <div className={styles.chartSettingsBody}>
                              <KeisukeDashboardChartSettingsSlot />
                            </div>
                          </div>
                          {renderSplitter('rightVert', 'h')}
                        </>
                      )}
                      <div
                        className={styles.paneTop}
                        style={{
                          flex: chartSettingsOpen ? `${rightChartSettingsRatio} 1 0` : '1 1 0',
                          minHeight: 0,
                        }}
                        aria-label={t('keisuke.dashboard.chartPaneLabel')}
                      >
                        <KeisukeDashboardChartPanel />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            </div>
          </KeisukeChartSettingsUiProvider>
        </KeisukeDashboardSurvivalChartSettingsProvider>
      </KeisukeDashboardDoseResponseSettingsProvider>
    </KeisukeDashboardBarChartSettingsProvider>
  );
}
